import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import config from '../config/config';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Package, Truck, CheckCircle, XCircle, Clock, Search, Filter, Eye, RotateCcw,
  Download, X,CreditCard
} from 'lucide-react';

import FlashBar from "../components/FlashBar";
import NavigationBar from "../components/NavigationBar";
import FooterSection from "../components/FooterSection";
import CartSection from "../components/CartSection";
import SearchWidget from "../components/SearchWidget";
import ShareableNav from "../components/ShareableNav";
// ────────────────────────────────────────────────────────────────────────────
// Cache Configuration
// ────────────────────────────────────────────────────────────────────────────
const CACHE_KEY_LAYOUT = 'layout_settings_cache_orders-01';
const CACHE_KEY_ORDERS  = 'orders_cache_user_orders-01';
const CACHE_TTL = 90 * 1000;          // 1.5 minutes for layout
const ORDERS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes for orders (more static)

const ShopPage = () => {
  const { order_id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
const userInfo = localStorage.getItem('userInfor');
   if(!userInfo){
  navigate('/Authentication');
}
  // Layout states
  const [isReadyToPay,setIsReadyToPay]=useState(false);
  const [mpesaMessage,setMpesaMessage]=useState('');
  const [mpesaLoading,setMpesaLoading]=useState(null)
  const [flashConfig, setFlashConfig] = useState(null);
  const [navConfig, setNavConfig] = useState(null);
  const [searchConfig, setSearchConfig] = useState(null);
  const [cartConfig, setCartConfig] = useState(null);
  const [footerConfig, setFooterConfig] = useState(null);

  // Orders & UI states
  const [orders, setOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState([]);
   const [currentOrderArray, setCurrentOrderArray] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  // ────────────────────────────────────────────────
  // Cache Helpers
  // ────────────────────────────────────────────────
  const getCached = (key, customTTL = CACHE_TTL) => {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;
      const { data, ts } = JSON.parse(item);
      if (Date.now() - ts < customTTL) return data;
      localStorage.removeItem(key);
      return null;
    } catch {
      localStorage.removeItem(key);
      return null;
    }
  };

  const setCached = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify({
        data,
        ts: Date.now()
      }));
    } catch (e) {
      // silent fail
    }
  };

  // ────────────────────────────────────────────────
  // Layout loading – cache first + background refresh
  // ────────────────────────────────────────────────

  // ────────────────────────────────────────────────
  // Load Orders – cache first + background refresh
  // ────────────────────────────────────────────────
  const loadOrders = useCallback(async () => {
    if (!token) return;

    // 1. Try cache first
    const cachedOrders = getCached(CACHE_KEY_ORDERS, ORDERS_CACHE_TTL);
    if (cachedOrders) {
      setOrders(cachedOrders);
    }

    try {
      // Try to get fresh data
      const ordersRes = await axios.get(
        `${config.API_URL}/api/pullOrders/${token}${order_id ? `/${order_id}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const freshOrders = ordersRes.data?.orders ||
                         ordersRes.data?.data   ||
                         ordersRes.data         ||
                         [];
         
//alert(ordersRes.data?.order_status);
      if(ordersRes.data?.order_status=='ready to pay'){
       
        setCurrentOrder(ordersRes.data?.current_order);
        setCurrentOrderArray(ordersRes.data?.currentOrderArray);

         setTimeout(function(){
       // console.log(currentOrder);
        //console.log(currentOrderArray);
         setIsReadyToPay(true);
       },500);
      }

      setOrders(freshOrders);
      setCached(CACHE_KEY_ORDERS, freshOrders);

     

    } catch (err) {
      console.error("Orders fetch failed:", err);
      if (!cachedOrders) {
        toast.error("Could not load your orders");
      }
    }
  }, [token, order_id]);


const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    setFormData(prev => {
        // We calculate using the NEW value that is about to be set
        

        // Return the new formData state
        return {
            ...prev,
            [name]: value
        };
    });
}, []);

   const [formData, setFormData] = useState({
    
    mpesaPhone: '',
  });

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // ────────────────────────────────────────────────
  // Computed values
  // ────────────────────────────────────────────────
const tabs = useMemo(() => [
  { key: 'all',        label: 'All Orders',       count: orders.length },
  { key: 'pending',    label: 'Pending',          count: orders.filter(o => o.status?.toLowerCase() === 'pending').length },   // ← Add comma here!
  { key: 'processing', label: 'Processing',       count: orders.filter(o => o.status?.toLowerCase() === 'processing').length },
  { key: 'ready to pay', label: 'Ready To Pay',   count: orders.filter(o => o.status?.toLowerCase() === 'ready to pay' || o.status?.toLowerCase() === 'ready to pay').length },
  { key: 'packaging',  label: 'Packaging',        count: orders.filter(o => o.status?.toLowerCase() === 'packaging').length },
  { key: 'shipped',    label: 'Shipped',          count: orders.filter(o => o.status?.toLowerCase() === 'shipped').length },
  { key: 'delivered',  label: 'Delivered',        count: orders.filter(o => o.status?.toLowerCase() === 'delivered').length },
  { key: 'canceled',   label: 'Canceled',         count: orders.filter(o => o.status?.toLowerCase() === 'canceled').length },
], [orders]);

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const matchesTab = activeTab === 'all' || order.status?.toLowerCase() === activeTab;
      const matchesSearch = searchQuery === '' ||
        order.id?.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.products?.some(p => p.name?.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesTab && matchesSearch;
    });
  }, [orders, activeTab, searchQuery]);

 const getStatusConfig = (status) => {
  const key = (status || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '');

  const configs = {
    delivered:  { label: 'Delivered',   icon: CheckCircle, color: 'text-green-600',  bg: 'bg-green-100' },
    shipped:    { label: 'Shipped',     icon: Truck,       color: 'text-blue-600',   bg: 'bg-blue-100'  },
    packaging:  { label: 'Packaging',   icon: Truck,       color: 'text-teal-600',   bg: 'bg-teal-100'  },
    processing: { label: 'Processing',  icon: Clock,       color: 'text-orange-600', bg: 'bg-orange-100'},
    pending:    { label: 'Pending',     icon: Clock,       color: 'text-orange-700', bg: 'bg-orange-200'},
    readytopay: { label: 'Ready to Pay', icon: CreditCard, color: 'text-orange-800', bg: 'bg-orange-100'},
    cancelled:  { label: 'Cancelled',   icon: XCircle,     color: 'text-red-600',    bg: 'bg-red-100'   },
  };

  return configs[key] || configs.processing;
};

const pollPayment = async () => {
    try {
     // setMpesaLoading(0);
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const endpoint =`/api/pollPayment`;

      const response = await axios.post(
        config.API_URL + endpoint,
        { phone: formData.mpesaPhone,order_id:order_id },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data.status == 'success') {
        setMpesaLoading('success');
         window.location.reload();
       setTimeout(function(){
        window.location.reload();
       },4000);
       // alert("Products section saved successfully! 🎉");
       // if (onSave) onSave(currentConfig);
      } else {
         setMpesaLoading('failed-2');
         setMpesaMessage(response.data.message);
      }
    } catch (error) {
    //setMpesaLoading('failed');
    } finally {
     // setSaving(false);
    }
  };

  const handleMpesaPrompt = async () => {
    try {
      setMpesaLoading(0);
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const endpoint =`/api/handleMpesaPrompt`;

      const response = await axios.post(
        config.API_URL + endpoint,
        { phone: formData.mpesaPhone,order_id:order_id },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data.status == '0') {
        setMpesaLoading(1);
        setInterval(pollPayment,2000);
       // alert("Products section saved successfully! 🎉");
       // if (onSave) onSave(currentConfig);
      } else {
         setMpesaLoading('failed');
      }
    } catch (error) {
    //setMpesaLoading('failed');
    } finally {
     // setSaving(false);
    }
  };


  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);

  return (
    <div className="flex flex-col mt-[24%] md:mt-[0px] min-h-screen bg-gray-50">
      <ShareableNav/>
      <main className="flex-grow">
        {/* Header */}
    <style>{`
       @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=Quicksand:wght@400;500;600;700&display=swap');
     html,body{
       font-family: ${config.FONT_FAMILY}, sans-serif !important;
     }
      `}
      </style>
     
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">My Orders</h1>
                <p className="text-gray-600">Track and manage your recent purchases</p>
              </div>

              <div className="flex gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search orders..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex overflow-x-auto gap-1 -mb-px">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 md:px-6 py-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab.key
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.key ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Orders Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          {filteredOrders.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Orders Found</h3>
              <p className="text-gray-600 mb-6">
                {searchQuery ? 'Try different search terms' : "You haven't placed any orders yet"}
              </p>
              <button
                onClick={() => navigate('/Shop/all')}
                className="bg-gray-900 text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                Start Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {filteredOrders.map(order => {
                const status = getStatusConfig(order.status);
                const StatusIcon = status.icon;

                return (
                  <div
                    key={order.id}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    {/* Header */}
                    <div className="p-5 md:p-6 border-b border-gray-200">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-semibold text-lg text-gray-900">{order.id}</h3>
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                            <StatusIcon size={14} />
                            {status.label}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {new Date(order.date).toLocaleDateString('en-KE', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5 md:p-6">
                      <div className="space-y-4 mb-6">
                        {order.products?.map((p, i) => (
                          <div key={i} className="flex gap-4">
                            <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                              <img
                                src={p.image || 'https://admins.junisaproducts.com/assets/images/icons/snacks.svg'}
                                alt={p.name}
                                className="w-full h-full object-cover"
                                onError={e => e.target.src = "https://placehold.co/80x80?text=?"}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-900 mb-1 line-clamp-2">{p.name}</h4>
                              <div className="text-sm text-gray-600">
                              
                                Quantity: {p.quantity} × {formatCurrency(p.price)} = {formatCurrency(p.quantity * p.price)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-gray-200">
                        <div>
                          <p className="text-sm text-gray-600">Total</p>
                          <p className="text-xl font-bold text-gray-900">{formatCurrency(order.total)}</p>
                        </div>

                        <div className="flex flex-wrap gap-3 w-full sm:w-auto">
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className="flex-1 sm:flex-none px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                          >
                            <Eye size={16} />
                            Details
                          </button>

                    {/*      {order.status?.toLowerCase() === 'shipped' && (
                            <button className="flex-1 sm:flex-none px-5 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2">
                              <Truck size={16} />
                              Track
                            </button>
                          )}

                          {order.status?.toLowerCase() === 'delivered' && (
                            <button className="flex-1 sm:flex-none px-5 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2">
                              <RotateCcw size={16} />
                              Reorder
                            </button>
                          )} */}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Order Detail Modal */}
          {isReadyToPay && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b z-10 p-6 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Your Order Is Ready For Payment</h2>
                  
                </div>
                {/*<button
                  onClick={() => setIsReadyToPay(null)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X size={24} className="text-gray-600" />
                </button>*/}
              </div>

              <div className="p-6 space-y-8">
                {/* Status */}
                <div>
                 
                {/* Summary */}
                <div>
                  <h3 className="font-semibold mb-3">Summary</h3>
                  <div className="bg-gray-50 rounded-xl p-5 space-y-3">
                    <div className="flex justify-between text-gray-700">
                      <span>Subtotal</span>
                      <span>{formatCurrency(currentOrder?.total_amount - (currentOrder?.delivery_fee || 0))}</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>Shipping</span>
                      <span>{formatCurrency(currentOrder?.delivery_fee || 0)}</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t font-bold text-lg">
                      <span>Total</span>
                      <span>{formatCurrency(currentOrder?.total_amount)}</span>
                    </div>
                  </div>
                </div>
                {mpesaLoading=='failed-2' && (
                      <div className="bg-red-400 text-white p-3 mt-3 rounded">
                     {mpesaMessage}
                      </div>
                    )}
                    {mpesaLoading=='success' && (
                      <div className="bg-green-700 text-white p-3 mt-3 rounded">
                     Payment made successfully. Page will refresh in a few.
                      </div>
                    )}
                 {mpesaLoading=='failed' && (
                      <div className="bg-red-400 text-white p-3 mt-3 rounded">
                     Sorry, something went wrong while processing your payment. Please retry.
                      </div>
                    )}

                 {mpesaLoading=='0' && (
                      <div className="bg-orange-400 text-white p-3 mt-3 rounded">
                     Processing your payment.  Do not refresh page until payment is complete
                      </div>
                    )}
                 {mpesaLoading=='1' && (
                      <div className="bg-green-500 text-white p-3 mt-3 rounded">
                      An STK push has been sent to your phone. Enter Mpesa pin to proceed. Do not refresh page until payment is complete
                      </div>
                    )}
                  
                   <div className="bg-blue-50 border border-green-700 rounded-xl mt-3 p-6">
                  
                <label className="block text-sm font-medium text-green-900 mb-2">M-Pesa Phone Number</label>
                <input
                  type="tel"
                  name="mpesaPhone"
                  value={formData.mpesaPhone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent bg-white"
                  placeholder="0712345678"
                />
                <p className="text-sm text-green-700 mt-2">
                  You'll receive an STK push on your phone to complete payment
                </p>
                 <button
                onClick={() => handleMpesaPrompt()}
                className="bg-green-700 text-white mt-4 px-8 py-3 rounded-lg font-medium hover:bg-green-800 transition-colors"
              >
               Make Payment

              </button>
              </div>
                </div>

                {/* Items */}
                <div>
                  <h3 className="font-semibold mb-3">Items</h3>
                  <div className="space-y-4">
                  {currentOrderArray?.[0]?.products?.map((p, i) => (
                      <div key={i} className="flex gap-4 p-4 bg-gray-50 rounded-xl">
                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                          <img
                            src={p.image || 'https://admins.junisaproducts.com/assets/images/icons/snacks.svg'}
                            alt={p.name}
                            className="w-full h-full object-cover"
                            onError={e => e.target.src = "https://placehold.co/80x80?text=?"}
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{p.name}</h4>
                          <div className="mt-1 text-sm text-gray-600">
                            Quantity: {p.quantity} × {formatCurrency(p.price)} = {formatCurrency(p.quantity * p.price)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div>
                  <h3 className="font-semibold mb-3">Summary</h3>
                  <div className="bg-gray-50 rounded-xl p-5 space-y-3">
                    <div className="flex justify-between text-gray-700">
                      <span>Subtotal</span>
                      <span>{formatCurrency(currentOrder?.total_amount - (currentOrder?.delivery_fee || 0))}</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>Shipping</span>
                      <span>{formatCurrency(currentOrder?.delivery_fee || 0)}</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t font-bold text-lg">
                      <span>Total</span>
                      <span>{formatCurrency(currentOrder?.total_amount)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {selectedOrder && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b z-10 p-6 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Order Details</h2>
                  <p className="text-gray-600">{selectedOrder.id}</p>
                </div>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X size={24} className="text-gray-600" />
                </button>
              </div>

              <div className="p-6 space-y-8">
                {/* Status */}
                <div>
                  <h3 className="font-semibold mb-3">Status</h3>
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${getStatusConfig(selectedOrder.status).bg}`}>
                    {React.createElement(getStatusConfig(selectedOrder.status).icon, {
                      className: `w-5 h-5 ${getStatusConfig(selectedOrder.status).color}`
                    })}
                    <span className={`font-medium ${getStatusConfig(selectedOrder.status).color}`}>
                      {getStatusConfig(selectedOrder.status).label}
                    </span>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <h3 className="font-semibold mb-3">Items</h3>
                  <div className="space-y-4">
                    {selectedOrder.products?.map((p, i) => (
                      <div key={i} className="flex gap-4 p-4 bg-gray-50 rounded-xl">
                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                          <img
                            src={p.image || 'https://admins.junisaproducts.com/assets/images/icons/snacks.svg'}
                            alt={p.name}
                            className="w-full h-full object-cover"
                            onError={e => e.target.src = "https://placehold.co/80x80?text=?"}
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{p.name}</h4>
                          <div className="mt-1 text-sm text-gray-600">
                            Quantity: {p.quantity} × {formatCurrency(p.price)} = {formatCurrency(p.quantity * p.price)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div>
                  <h3 className="font-semibold mb-3">Summary</h3>
                  <div className="bg-gray-50 rounded-xl p-5 space-y-3">
                    <div className="flex justify-between text-gray-700">
                      <span>Subtotal</span>
                      <span>{formatCurrency(selectedOrder.total - (selectedOrder.shipping_amount || 0))}</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>Shipping</span>
                      <span>{formatCurrency(selectedOrder.shipping_amount || 0)}</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t font-bold text-lg">
                      <span>Total</span>
                      <span>{formatCurrency(selectedOrder.total)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
<br/><br/><br/>
      {footerConfig && <FooterSection config={footerConfig} />}
    </div>
  );
};

export default ShopPage;